import React from 'react';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { VoucherCampaignFormFields } from '../form';
import { graphql } from '../../../generated';
import { VoucherCampaign } from '../../../generated/graphql';
import { useModal } from '../../../components/Modal/PresentedModal';
import { t } from '../../../locales/translate';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { handleError } from '../../../model/notification';
import { FormForm } from '../../../components/Form/FormForm';

export const VOUCHER_CAMPAIGN_FOR_UPDATE = graphql(`
  query VoucherCampaignForUpdate($id: ID!) {
    voucherCampaign(id: $id) {
      id
      title
      internalTitle
      discountType
      amount
      usage
      target
      costSplit
      shopId
      shop {
        id
        name
      }
    }
  }
`);

const UPDATE_VOUCHER_CAMPAIGN = graphql(`
  mutation UpdateVoucherCampaign(
    $id: ID!
    $shopId: ID!
    $title: String!
    $internalTitle: String
    $target: VoucherCampaignTarget!
    $amount: Float!
    $discountType: VoucherCampaignDiscountType!
    $costSplit: Float
    $usage: VoucherCampaignUsage!
  ) {
    updateVoucherCampaign(
      input: {
        id: $id
        shopId: $shopId
        title: $title
        internalTitle: $internalTitle
        target: $target
        amount: $amount
        discountType: $discountType
        costSplit: $costSplit
        usage: $usage
      }
    ) {
      voucherCampaign {
        id
        shopId
        title
        internalTitle
        target
        amount
        discountType
        costSplit
        usage
        shop {
          id
          name
        }
      }
    }
  }
`);

interface Props {
  id: string;
  onClose?: () => void;
}

export function VoucherCampaignUpdate({ id, onClose }: Props): JSX.Element {
  const { data } = useSuspenseQuery(VOUCHER_CAMPAIGN_FOR_UPDATE, { variables: { id } });

  const { open, Modal, close } = useModal();

  const [update] = useMutation(UPDATE_VOUCHER_CAMPAIGN);

  const { fields, dirty, valid, onSubmit } = useForm<Partial<VoucherCampaign>>({
    model: {
      shopId: data.voucherCampaign.shopId,
      title: data.voucherCampaign.title,
      internalTitle: data.voucherCampaign.internalTitle,
      target: data.voucherCampaign.target,
      discountType: data.voucherCampaign.discountType,
      amount: data.voucherCampaign.amount,
      costSplit: data.voucherCampaign.costSplit,
      usage: data.voucherCampaign.usage,
    },
    onSubmit: async ({ model }) => {
      await update({
        variables: {
          id,
          shopId: model.shopId,
          title: model.title,
          internalTitle: model.internalTitle,
          target: model.target,
          discountType: model.discountType,
          amount: model.amount,
          costSplit: model.costSplit,
          usage: model.usage,
        } as VoucherCampaign,
      });
      onClose?.();
      close();
    },
    validations: {
      shopId: 'required',
      title: 'required',
      target: 'required',
      discountType: 'required',
      amount: [
        'required',
        ({ model }) => {
          if (
            model.amount !== undefined &&
            model.discountType === 'PERCENTAGE' &&
            (model.amount < 0 || model.amount > 1)
          ) {
            return [t.page.voucherCampaigns.form.amountPercentageError];
          }
          return [];
        },
      ],
      costSplit: [
        'required',
        ({ model }) => {
          if (model.costSplit !== undefined && (model.costSplit < 0 || model.costSplit > 1)) {
            return [t.page.voucherCampaigns.form.amountPercentageError];
          }
          return [];
        },
      ],
      usage: 'required',
    },
    onSubmitError: handleError,
  });

  return (
    <Modal open={open} title={t.page.voucherCampaigns.form.edit}>
      <FormForm onSubmit={onSubmit} onClose={close} canSubmit={dirty && valid}>
        <VoucherCampaignFormFields fields={fields} />
      </FormForm>
    </Modal>
  );
}
